import React from 'react';

import CardDialog from '~/components/CardDialog';
import InnerTabs from '~/components/core/Layout/InnerTabs/InnerTabs';
import ConfigurationAssistantTab from '~/components/SystemConfiguration/ConfigurationManagement/Assistant/ConfigurationAssistantTab';
import { ASSISTANT_TEXTS } from '~/components/SystemConfiguration/ConfigurationManagement/Assistant/texts';
import AuditLogTab from '~/components/SystemConfiguration/ConfigurationManagement/AuditLog/AuditLogTab';
import ChangeRequests from '~/components/SystemConfiguration/ConfigurationManagement/ChangeRequests/ChangeRequests';
import PromotionsTab from '~/components/SystemConfiguration/ConfigurationManagement/Promotions/PromotionsTab';
import ConfigurationSnapshots from '~/components/SystemConfiguration/ConfigurationSnapshots';
import OperationsBreadcrumbs from '~/components/SystemConfiguration/OperationsBreadcrumbs';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { UserModel } from '~/components/types/user-types';
import useDataFetcher from '~/components/useDataFetcher';
import type { AuditedConfigurationChangeList } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

interface ConfigurationManagementProps {
  // Promotions is available internally (super-org) or externally when the CONFIGURATION_MANAGEMENT
  // feature flag is on. Snapshots & Backup is Five Sigma-only: it shows in the internal admin exclusively.
  isInternal?: boolean;
}

const ConfigurationManagement: React.FC<ConfigurationManagementProps> = ({ isInternal = false }) => {
  // @ts-expect-error TODO: useSysconfig hook needs TypeScript definition
  const { organization } = useSysconfig();

  // Snapshots & Backup: Five Sigma-only, shown exclusively in the internal admin.
  const showSnapshots = isInternal;
  // Promotions: open internally, or externally when the CONFIGURATION_MANAGEMENT flag is on.
  const showPromotions =
    isInternal || isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONFIGURATION_MANAGEMENT);
  // Configuration Assistant: a POC open to Five Sigma staff only, so internally alone, when CONFIGURATION_ASSISTANT is
  // on for the organization being configured, the one its routes gate on and its runs work on.
  const showAssistant =
    isInternal && isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONFIGURATION_ASSISTANT);
  // Audit Log + Change Requests are gated by a feature flag (both internally and externally).
  const showAuditAndChangeRequests = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.STARR_DEMO_AUDIT_LOG);

  // Fetch configuration changes (audit log) only when those tabs are shown.
  const {
    isLoading: isLoadingChanges,
    isError: isErrorChanges,
    data: configurationChanges = [],
    reloadData: reloadConfigurationChanges,
  } = useDataFetcher<AuditedConfigurationChangeList>(
    `/sysconfig/api/v1/organizations/${organization.id}/change_requests/audit_log/configuration_changes`,
    null,
    showAuditAndChangeRequests
  );

  // Fetch users for display (only needed by the audit/change-request tabs).
  const {
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
    data: users,
  } = useDataFetcher<UserModel[]>(
    `/sysconfig/api/v1/organizations/${organization.id}/users`,
    { params: { include_deactivated: true, include_licenses: false } },
    showAuditAndChangeRequests
  );

  const tabs = [
    // Snapshots & Backup (internal-only) and Promotions lead the tab list.
    ...(showSnapshots
      ? [
          {
            label: 'Snapshots & Backup',
            url: 'snapshots',
            component: ConfigurationSnapshots,
          },
        ]
      : []),
    ...(showPromotions
      ? [
          {
            label: 'Promotions',
            url: 'promotions',
            component: PromotionsTab,
            // Thread isInternal via the stable `props` channel (not an inline component) so the tab isn't
            // a fresh type each render — an inline `() => <PromotionsTab .../>` would remount the route on
            // every parent re-render, wiping tab state and refetching. isInternal gates the tab's
            // internal-only "Promote from file" upload entry point; the tab itself still shows externally
            // via the CONFIGURATION_MANAGEMENT flag.
            props: { isInternal },
          },
        ]
      : []),
    ...(showAssistant
      ? [
          {
            label: ASSISTANT_TEXTS.TAB_LABEL,
            url: 'assistant',
            component: ConfigurationAssistantTab,
          },
        ]
      : []),
    ...(showAuditAndChangeRequests
      ? [
          {
            label: 'Audit Log',
            url: 'audit_log',
            component: () => (
              <AuditLogTab
                configurationChanges={configurationChanges}
                users={users}
                isLoadingChanges={isLoadingChanges}
                isErrorChanges={isErrorChanges}
                isLoadingUsers={isLoadingUsers}
                isErrorUsers={isErrorUsers}
                reloadConfigurationChanges={reloadConfigurationChanges}
              />
            ),
          },
          {
            label: 'Change Requests',
            url: 'change_requests',
            component: () => (
              <ChangeRequests
                configurationChanges={configurationChanges}
                users={users}
                isLoadingChanges={isLoadingChanges}
                isErrorChanges={isErrorChanges}
                isLoadingUsers={isLoadingUsers}
                isErrorUsers={isErrorUsers}
                reloadConfigurationChanges={reloadConfigurationChanges}
                organization={organization}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <CardDialog noCardTitle>
      <OperationsBreadcrumbs currentTab="Configuration Management" />
      <InnerTabs tabs={tabs} />
    </CardDialog>
  );
};

export default ConfigurationManagement;
